import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#bbe425] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-[#095D52] text-[#ede8c5] border border-[#095D52] shadow hover:bg-[#bbe425] hover:text-[#0B222C] hover:border-[#bbe425]',
        destructive: 'bg-[#EF4444] text-white shadow-sm hover:bg-[#dc2626]',
        outline:
          'border border-[#095D52] bg-transparent text-[#ede8c5] shadow-sm hover:bg-[#095D52] hover:text-[#ede8c5]',
        secondary: 'bg-[#0e3d37] text-[#ede8c5] shadow-sm hover:bg-[#095D52]',
        ghost: 'text-[#ede8c5] hover:bg-[#095D52] hover:text-[#ede8c5]',
        link: 'text-[#bbe425] underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
